/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '../../contexts/ThemeContext';
import SubmitButton from '../buttons/SubmitButton';
import SelectField from '../fields/Select';
import { useThemeClassName } from '../../contexts/ThemeContext';

import styles from '../../../styles/forms/displays.module.scss';

const propTypes = {
    displays: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    games: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ),
    submitLabel: PropTypes.string,
    withoutRegions: PropTypes.bool,
    displaysPlaceholder: PropTypes.node,
    gamesPlaceholder: PropTypes.node,
    regionsPlaceholder: PropTypes.node,
    className: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    displays: null,
    games: null,
    submitLabel: null,
    withoutRegions: false,
    displaysPlaceholder: null,
    gamesPlaceholder: null,
    regionsPlaceholder: null,
    className: null,
    onSuccess: null,
};

function DisplaysForm({
    displays,
    games: initialGames,
    submitLabel,
    withoutRegions,
    displaysPlaceholder,
    gamesPlaceholder,
    regionsPlaceholder,
    className,
    onSuccess,
}) {
    const intl = useIntl();
    const themeClassName = useThemeClassName(styles);
    const [display, setDisplay] = useState(null);
    const [game, setGame] = useState(null);
    const [games, setGames] = useState(initialGames);

    const [region, setRegion] = useState(null);
    const regions = useMemo(
        () =>
            Object.keys(
                (displays || []).reduce((acc, dspl) => {
                    const { venue } = dspl || {};
                    const { region: venueRegion = null } = venue || {};
                    if (typeof acc[venueRegion] === 'undefined') {
                        acc[venueRegion] = region;
                    }
                    return acc;
                }, {}),
            )
                .filter((k) => k !== null && k !== '')
                .map((k) => ({ id: k, label: k, value: k })),
        [],
    );
    const onRegionChange = useCallback(
        (val) => {
            setRegion(val || null);
            setDisplay(null);
            setGame(null);
        },
        [setRegion, setDisplay, setGame],
    );

    const finalDisplays = useMemo(
        () =>
            (displays || [])
                .map(
                    ({
                        id = null,
                        title = null,
                        games: displayGames = null,
                        venue = null,
                        slug = null,
                    } = {}) => ({
                        label: `${title}${venue !== null && venue.city ? ` - ${venue.city}` : ''}${
                            venue !== null && venue.region ? ` - ${venue.region}` : ''
                        }`,
                        value: slug || id,
                        region:
                            venue !== null && venue.region ? venue !== null && venue.region : null,
                        games: displayGames || games,
                    }),
                )
                .filter(
                    ({ region: displayRegion = null }) =>
                        region === null || displayRegion === null || region === displayRegion,
                ),
        [displays, region, games],
    );

    const finalGames = useMemo(
        () => (games || []).map((g) => ({ label: g.title, value: g.id })),
        [games],
    );

    const onDisplayChange = useCallback(
        (val) => {
            const newDisplay = finalDisplays.find((v) => v.value === val) || null;
            // console.log('val', val);
            setDisplay(newDisplay);
            if (newDisplay !== null) {
                const { games: displayGames = [] } = newDisplay || {};
                setGame(null);
                setGames(displayGames || []);
            }
        },
        [finalDisplays, setDisplay, setGames, setGame],
    );

    const onGameChange = useCallback(
        (val) => {
            const newGame = finalGames.find((g) => g.value === val) || null;
            setGame(newGame !== null ? newGame : null);
        },
        [finalGames, setGame],
    );

    const onFormSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (onSuccess !== null) {
                onSuccess({
                    display: display !== null ? display.value : null,
                    game: game !== null ? game.value : null,
                });
            }
        },
        [display, game, onSuccess],
    );

    const disabled = display === null || (finalGames.length > 0 ? game === null : false);

    return (
        <form
            className={classNames([
                styles.container,
                themeClassName,
                className,
            ])}
            onSubmit={onFormSubmit}
            action={null}
        >
            <div className={styles.inner}>
                <div className={styles.fields}>
                    {!withoutRegions ? (
                        <div className={styles.formGroup}>
                            <SelectField
                                className={styles.select}
                                value={region}
                                items={regions}
                                onChange={onRegionChange}
                                placeholder={
                                    regionsPlaceholder ||
                                    intl.formatMessage({
                                        defaultMessage: 'Select your province',
                                        description: 'Form placeholder',
                                    })
                                }
                            />
                        </div>
                    ) : null}

                    <div className={styles.formGroup}>
                        <SelectField
                            className={styles.select}
                            value={display !== null ? display?.value : null}
                            items={finalDisplays}
                            onChange={onDisplayChange}
                            placeholder={
                                displaysPlaceholder ||
                                intl.formatMessage({
                                    defaultMessage: 'Select your establishment',
                                    description: 'Form placeholder',
                                })
                            }
                            isRequired
                        />
                    </div>
                    {finalGames.length > 0 ? (
                        <div className={styles.formGroup}>
                            <SelectField
                                className={styles.select}
                                value={game !== null ? game?.value : null}
                                items={finalGames}
                                onChange={onGameChange}
                                placeholder={
                                    gamesPlaceholder ||
                                    intl.formatMessage({
                                        defaultMessage: 'Select your game',
                                        description: 'Form placeholder',
                                    })
                                }
                            />
                        </div>
                    ) : null}
                </div>
                <SubmitButton
                    className={classNames([
                        styles.submit,
                        {
                            [styles.disabled]: disabled,
                        },
                    ])}
                    disabled={disabled}
                >
                    {submitLabel !== null ? (
                        <p>{submitLabel}</p>
                    ) : (
                        <FormattedMessage defaultMessage="Start" description="Button label" />
                    )}
                </SubmitButton>
            </div>
        </form>
    );
}

DisplaysForm.propTypes = propTypes;
DisplaysForm.defaultProps = defaultProps;

export default DisplaysForm;
